import React, { useCallback, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

import { isImage } from 'helpers/utils';
import { Icon, Modal } from 'ui';

import Thumbnail from './Thumbnail';
import ThumbnailsWrapper from './ThumbnailsWrapper';

type Props = {
  fileUploads: Backend.Backend.FileUpload[];
  disablePreview?: boolean;
};

type GalleryNavButtonProps = {
  icon: React.ComponentProps<typeof Icon>['name'];
  onClick: () => void;
  disabled: boolean;
  direction: 'left' | 'right';
};

function GalleryNavButton({ icon, onClick, disabled, direction }: GalleryNavButtonProps) {
  const classArray = [
    'inline-flex',
    'items-center',
    'font-medium',
    'whitespace-nowrap',
    'text-base',
    'image-gallery-icon',
    `image-gallery-${direction}-nav`,
    'hover:text-indigo-500',
  ];

  const iconClassArray = ['h-16', 'w-16', 'px-1.5', 'py-2.5'];

  if (disabled) {
    classArray.push('opacity-50', 'cursor-not-allowed');
  }

  return (
    <button type="button" className={classArray.join(' ')} disabled={disabled} onClick={onClick}>
      <Icon name={icon} size="huge" className={iconClassArray.join(' ')} />
    </button>
  );
}

function Thumbnails({ fileUploads, disablePreview = false }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // used gallery library - https://github.com/xiaolin/react-image-gallery
  const galleryRef = useCallback(
    (galleryNode) => {
      if (galleryNode) {
        galleryNode.slideToIndex(selectedIdx);
      }
    },
    [selectedIdx],
  );

  const imageFileUploads = fileUploads.filter((fileUpload) => isImage(fileUpload.filename));

  const handleFileUploadClick = (fileUpload: Backend.FileUpload) => {
    const fileUploadIdx = imageFileUploads.findIndex((item) => item.id === fileUpload.id);
    if (fileUploadIdx !== -1) {
      setIsModalOpen(true);
      setSelectedIdx(fileUploadIdx);
    }
  };

  return (
    <>
      <ThumbnailsWrapper>
        {fileUploads.map((item) => (
          <Thumbnail
            key={item.id}
            disablePreview={disablePreview}
            fileUpload={item}
            onClick={() => handleFileUploadClick(item)}
          />
        ))}
      </ThumbnailsWrapper>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} size="7xl">
        <div className="react-image-gallery-wrapper rounded-lg bg-gray-200 px-4 pt-4">
          <ImageGallery
            ref={galleryRef}
            items={imageFileUploads.map((fileUpload) => ({
              original: fileUpload.file,
              thumbnail: `${fileUpload.file}`,
              originalTitle: fileUpload.filename,
              originalAlt: fileUpload.filename,
              thumbnailTitle: fileUpload.filename,
              thumbnailAlt: fileUpload.filename,
            }))}
            showPlayButton={false}
            showFullscreenButton={false}
            infinite={false}
            renderLeftNav={(onClick, disabled) => (
              <GalleryNavButton icon="arrow-left" onClick={onClick} disabled={disabled} direction="left" />
            )}
            renderRightNav={(onClick, disabled) => (
              <GalleryNavButton icon="arrow-right" onClick={onClick} disabled={disabled} direction="right" />
            )}
          />
        </div>
      </Modal>
    </>
  );
}

export default Thumbnails;
